import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { next } from '@ember/runloop';
import { modifier } from 'ember-modifier';
import { trackedFunction } from 'reactiveweb/function';
import ComboChart from 'tio-ui/components/charts/combo-chart';
import Chart from 'tio-ui/components/chart';
import SummaryDisplay from 'tio-ui/components/charts/summary-display';
import { DEFAULT_CHART_COLORS } from 'tio-ui/utils/charts/color-palette';
import PreviewTable from '../report-builder/preview-table.gts';
import HorizontalBarsChart from 'tio-ui/components/charts/horizontal-bars-chart';
import {
  runWidget,
  sqlScopeUnavailable,
  SQL_SCOPE_REQUIRED_MESSAGE,
  type ComboData,
  type WidgetChartData,
} from '../../utils/dashboard-builder/widget-data-adapter.ts';
import {
  resolveDatasetFields,
  toResolvedWidget,
} from '../../utils/dashboard-builder/widget-resolver.ts';
import {
  formatNumberValue,
  getDefaultNumberFormat,
  type NumberFormatConfig,
} from '../../utils/column-format-options.ts';
import {
  getCompanyFilterField,
  getModelTypeFromDataset,
  getSearchRequestModelType,
} from '../../utils/dataset-utils.ts';
import type { FieldOption } from '../../utils/opensearch-field-mapper.ts';
import { parseSortString, type SortEntry } from '../../utils/sort-helpers.ts';
import type {
  WidgetConfig,
  BuildFilterConfig,
  TableWidgetConfig,
  UserFilterConfig,
} from '../../utils/dashboard-builder/widget-config-types.ts';
import { effectiveWidgetFilters } from '../../utils/dashboard-builder/effective-widget-filters.ts';
import {
  barDrilldownOptions,
  effectiveDateInterval,
  rowDrilldownOptions,
  selectionForCategory,
  type CategoryKey,
  type Drilldown,
  type DrilldownMode,
  type DrilldownOption,
} from '../../utils/dashboard-builder/drilldown.ts';
import { categoryLabeler } from '../../utils/dashboard-builder/widget-data-adapter.ts';
import DrilldownMenu from './drilldown-menu.gts';
import type { BarHit, BarSelection, ChartDataPoint } from 'tio-ui/types/charts';
import type { Store as StoreService } from '@warp-drive/core';

// WidgetRenderer – resolves a widget's dataset fields, runs its aggregation(s),
// and renders a counter (SummaryDisplay) or a chart (ComboChart), applying the
// widget's format layer (titles, value formats, legend, data labels, colors).

interface WidgetRendererSignature {
  Args: {
    widget: WidgetConfig;
    companyId?: string;
    buildFilters?: BuildFilterConfig;
    userFilters?: UserFilterConfig;
    /**
     * Chart height in px. Bar charts paint at it first, then fill the host box (which must
     * have a definite height) unless the widget sets its own `format.height`.
     */
    height?: number;
    /** Reports the rendered series labels (for the editor's color controls). */
    onSeries?: (labels: string[]) => void;
    /** Active dashboard drilldowns; only those for this widget's dataset (and not its own) apply. */
    drilldowns?: Drilldown[];
    /** Receives the drilldown a click on this widget's breakdown stands for (view mode). */
    onDrilldown?: (drilldown: Drilldown, mode: DrilldownMode) => void;
  };
  Element: HTMLDivElement;
}

function numberFormatter(config?: NumberFormatConfig): (v: number) => string {
  const cfg = config ?? getDefaultNumberFormat();
  // Dashboard number formatting defaults units to 'auto' so large values compact
  // (e.g. 5.0M) instead of overflowing the axis / counter.
  const withUnit: NumberFormatConfig = { ...cfg, unit: cfg.unit ?? 'auto' };
  return (v: number) => formatNumberValue(v, withUnit);
}

export default class WidgetRenderer extends Component<WidgetRendererSignature> {
  @service declare store: StoreService;

  #lastSeriesKey = '';
  #dataCache?: { key: string; value: WidgetChartData | null };
  #tableFieldsCache?: { key: string; value: FieldOption[] };
  #resolvedFields: FieldOption[] = [];

  /** Every filter input merged the one way both rendering and export use. */
  get effectiveFilters() {
    return effectiveWidgetFilters(this.args.widget, {
      buildFilters: this.args.buildFilters,
      userFilters: this.args.userFilters,
      drilldowns: this.args.drilldowns,
    });
  }

  /** The drilldown entries this widget receives, as filter values. */
  get drilldownFilterValues(): Record<string, unknown> {
    return this.effectiveFilters.extraFilterValues;
  }

  /**
   * A signature of only the inputs that affect the widget's query — dataset,
   * model toggle, wells, and filters. Layout/label/format/colors are excluded
   * so resizing or moving a widget (which replaces the widget object) does not
   * re-issue the OpenSearch request; trackedFunction still re-runs but the
   * memo guard below returns the cached result without a network call.
   */
  get dataKey(): string {
    const w = this.args.widget;
    return JSON.stringify({
      type: w.widgetType,
      dataset: w.dataset,
      individual: w.showIndividualApplicationsOnly ?? true,
      measure: w.measure,
      xDimension: w.xDimension,
      groupDimension: w.groupDimension,
      bars: w.bars,
      lines: w.lines,
      sliceDimension: w.sliceDimension,
      valueMeasure: w.valueMeasure,
      buildFilter: w.buildFilter,
      limit: w.limit,
      companyId: this.args.companyId,
      buildFilters: this.args.buildFilters,
      userFilters: this.args.userFilters,
      // Scoped per widget so a drilldown re-queries only the tiles it applies to.
      drilldown: this.drilldownFilterValues,
    });
  }

  /** Field-registry fetch depends only on dataset + model toggle + company scope. */
  get tableFieldsKey(): string {
    const w = this.args.widget;
    return JSON.stringify({
      dataset: w.dataset,
      individual: w.showIndividualApplicationsOnly ?? true,
      companyId: this.args.companyId,
    });
  }

  data = trackedFunction(this, async () => {
    const key = this.dataKey;
    if (this.#dataCache?.key === key) return this.#dataCache.value;

    const widget = this.args.widget;
    // Tables render via PreviewTable (self-fetching) — no aggregation pipeline.
    if (widget.widgetType === 'table') {
      this.#dataCache = { key, value: null };
      return null;
    }
    // Unqueryable config (see scopeUnavailable): the template renders the
    // reason, so stop before resolveDatasetFields. Bailing here rather than
    // relying on the template branch matters because the series-reporting
    // modifier consumes this resource outside it.
    if (sqlScopeUnavailable(this.tableModelType, this.args.companyId)) {
      this.#dataCache = { key, value: null };
      return null;
    }
    const companyId = this.args.companyId;
    const buildFilters = this.args.buildFilters;
    const userFilters = this.args.userFilters;
    const drilldowns = this.args.drilldowns;

    const fields = await resolveDatasetFields(
      this.store,
      widget.dataset,
      companyId,
      widget.showIndividualApplicationsOnly ?? true
    );
    this.#resolvedFields = fields;
    const resolved = toResolvedWidget(widget, fields, {
      companyId,
      buildFilters,
      userFilters,
      drilldowns,
    });
    const value = await runWidget(this.store, resolved);
    this.#dataCache = { key, value };
    return value;
  });

  get isLoading() {
    if (this.isTable) return this.tableFields.isLoading && !this.tableFields.value;
    return this.data.isLoading && !this.data.value;
  }

  // ── table widgets — column keys resolved against the dataset registry, the
  // table itself rendered by the report builder's self-fetching PreviewTable ──
  get isTable() {
    return this.args.widget.widgetType === 'table';
  }

  /** Chart widget rendered with horizontal bars (bars only — no lines). */
  get isHorizontalChart() {
    return this.args.widget.widgetType === 'chart' && this.args.widget.orientation === 'horizontal';
  }

  tableFields = trackedFunction(this, async () => {
    const widget = this.args.widget;
    if (widget.widgetType !== 'table') return [];
    const key = this.tableFieldsKey;
    if (this.#tableFieldsCache?.key === key) return this.#tableFieldsCache.value;
    const value = await resolveDatasetFields(
      this.store,
      widget.dataset,
      this.args.companyId,
      widget.showIndividualApplicationsOnly ?? true
    );
    this.#tableFieldsCache = { key, value };
    return value;
  });

  /** Viewer override, tied to the table config it was made on: any edit or dataset switch replaces that object. */
  @tracked tableView?: {
    source: TableWidgetConfig | undefined;
    sorts?: SortEntry[];
    limit?: number;
    page: number;
  };

  get tableConfig() {
    return this.args.widget.table;
  }

  get tableSavedSorts(): SortEntry[] {
    return this.tableConfig?.sorts ?? [];
  }

  get tableSavedLimit() {
    return this.tableConfig?.pageSize ?? 10;
  }

  get activeTableView() {
    const view = this.tableView;
    return view?.source === this.tableConfig ? view : undefined;
  }

  get tableSorts(): SortEntry[] {
    return this.activeTableView?.sorts ?? this.tableSavedSorts;
  }

  get tableLimit() {
    return this.activeTableView?.limit ?? this.tableSavedLimit;
  }

  get tablePage() {
    return this.activeTableView?.page ?? 1;
  }

  get tableColumns(): FieldOption[] {
    const fields = this.tableFields.value ?? [];
    const byKey = new Map(fields.map((f) => [f.key, f]));
    return (this.tableConfig?.selectedColumns ?? [])
      .map((k) => byKey.get(k))
      .filter(Boolean) as FieldOption[];
  }

  get tableAllFields(): FieldOption[] {
    return this.tableFields.value ?? [];
  }

  get tableModelType() {
    return getSearchRequestModelType(
      getModelTypeFromDataset(this.args.widget.dataset),
      this.args.widget.showIndividualApplicationsOnly ?? true
    );
  }

  get tableCompanyFilterField() {
    return getCompanyFilterField(this.args.widget.dataset);
  }

  get tableIsAggregated() {
    return Boolean(this.tableConfig?.groupBy?.selectedFields?.length);
  }

  /**
   * Replaces the view-time sort and returns the table to its first page.
   * @param sortField PreviewTable token ending in :asc or :desc; the field may contain colons.
   */
  updateTableSort = (sortField: string) => {
    const sort = parseSortString(sortField);
    if (!sort.key) return;
    this.tableView = { ...this.activeTableView, source: this.tableConfig, sorts: [sort], page: 1 };
  };

  updateTablePage = (page: number) => {
    this.tableView = { ...this.activeTableView, source: this.tableConfig, page };
  };

  updateTableLimit = (limit: number) => {
    this.tableView = { ...this.activeTableView, source: this.tableConfig, limit, page: 1 };
  };

  get isCounter() {
    return this.args.widget.widgetType === 'counter';
  }

  get counterPoints() {
    const v = this.data.value;
    if (v?.type !== 'counter') return [];
    return [{ category: this.args.widget.label ?? '', value: v.value }];
  }

  /**
   * Sub-label under the counter value. The tile header already shows the title,
   * so the value line carries the optional description instead (empty string ⇒
   * SummaryDisplay hides the line rather than falling back to "Total").
   */
  get counterSubLabel() {
    return this.args.widget.description ?? '';
  }

  get combo(): ComboData {
    const v = this.data.value;
    if (v?.type === 'combo') return v;
    return {
      type: 'combo',
      labels: [],
      keys: [],
      barSeries: [],
      lineSeries: [],
      stacked: true,
      secondaryAxis: true,
    };
  }

  get isPie() {
    return this.args.widget.widgetType === 'pie';
  }

  get piePoints() {
    const v = this.data.value;
    return v?.type === 'pie' ? v.points : [];
  }

  // ── drilldowns ──────────────────────────────────────────────────────────────
  get canDrilldown() {
    return Boolean(this.args.onDrilldown);
  }

  /** Only grouped rows stand for a dimension value; raw rows are records. */
  get canRowDrilldown() {
    return this.canDrilldown && this.tableIsAggregated;
  }

  @tracked menuAnchor?: { x: number; y: number };
  @tracked menuOptions: DrilldownOption[] = [];

  /** Drilldowns this widget emitted, for highlighting the clicked value. */
  get sourceDrilldowns() {
    return (this.args.drilldowns ?? []).filter((d) => d.sourceWidgetId === this.args.widget.id);
  }

  /** The exact pie point behind this widget's own drilldown: by raw key, or the synthetic "Other" slice. */
  get selectedPiePoint() {
    const dimension = this.args.widget.sliceDimension;
    if (!dimension) return undefined;
    const drilldown = this.sourceDrilldowns.find((d) => d.fieldKey === dimension.fieldKey);
    if (!drilldown) return undefined;
    return this.piePoints.find((p) =>
      drilldown.selection.kind === 'in' || drilldown.selection.kind === 'ranges'
        ? Boolean(p.metadata?.keys)
        : p.metadata?.key === drilldown.categoryKey
    );
  }

  /** Legend rows stand for group values, so only a grouped bar chart can drill from them. */
  get canLegendDrilldown() {
    return this.canDrilldown && Boolean(this.args.widget.groupDimension);
  }

  get selectedBar(): BarSelection | undefined {
    const { xDimension, groupDimension } = this.args.widget;
    const selection: BarSelection = {};
    const x = xDimension && this.sourceDrilldowns.find((d) => d.fieldKey === xDimension.fieldKey);
    if (x?.categoryKey !== undefined) {
      const index = this.combo.keys.indexOf(x.categoryKey);
      if (index >= 0) selection.index = index;
    }
    const group =
      groupDimension && this.sourceDrilldowns.find((d) => d.fieldKey === groupDimension.fieldKey);
    if (group?.categoryKey !== undefined) {
      const seriesIndex = this.combo.barSeries.findIndex((s) => s.key === group.categoryKey);
      if (seriesIndex >= 0) selection.seriesIndex = seriesIndex;
    }
    return selection.index === undefined && selection.seriesIndex === undefined
      ? undefined
      : selection;
  }

  fieldFor(fieldKey: string) {
    return [...this.#resolvedFields, ...(this.tableFields.value ?? [])].find(
      (f) => f.key === fieldKey
    );
  }

  get optionContext() {
    return {
      widget: this.args.widget,
      fieldLabelFor: (fieldKey: string) => this.fieldFor(fieldKey)?.label,
      isDateField: (fieldKey: string) => Boolean(this.fieldFor(fieldKey)?.isDate),
    };
  }

  get barData() {
    return { keys: this.combo.keys, labels: this.combo.labels, series: this.combo.barSeries };
  }

  /** A slice click drills on the slice dimension; "Other" excludes the shown slices. */
  handlePieClick = (point: ChartDataPoint) => {
    const widget = this.args.widget;
    const dimension = widget.sliceDimension;
    if (!dimension || !this.args.onDrilldown) return;
    const field = this.fieldFor(dimension.fieldKey);
    const interval = effectiveDateInterval(dimension.dateInterval, Boolean(field?.isDate));
    const category = point.metadata as CategoryKey | undefined;
    const selection = selectionForCategory(category, interval);
    if (!selection) return;
    this.args.onDrilldown(
      {
        sourceWidgetId: widget.id,
        dataset: widget.dataset,
        fieldKey: dimension.fieldKey,
        selection,
        valueLabel: point.category,
        fieldLabel: field?.label,
        categoryKey: category?.key,
      },
      'toggle'
    );
  };

  handleBarClick = (hit: BarHit) => {
    const options = barDrilldownOptions(this.optionContext, this.barData, hit);
    this.#offer(options, { x: hit.clientX, y: hit.clientY });
  };

  /** A legend entry is a group value, so it offers the group drilldown alone. */
  handleLegendClick = (seriesIndex: number) => {
    const options = barDrilldownOptions(this.optionContext, this.barData, {
      index: -1,
      seriesIndex,
    });
    this.#offer(options, undefined);
  };

  handleRowClick = (row: Record<string, unknown>, event: MouseEvent) => {
    const options = rowDrilldownOptions(this.optionContext, row, (fieldKey, raw) =>
      categoryLabeler(this.fieldFor(fieldKey))(raw)
    );
    this.#offer(options, { x: event.clientX, y: event.clientY });
  };

  /** One option applies at once; several open the menu at the pointer. */
  #offer(options: DrilldownOption[], anchor: { x: number; y: number } | undefined) {
    const [only] = options;
    if (!only) return;
    if (options.length === 1 || !anchor) {
      this.applyOption(only);
      return;
    }
    this.menuOptions = options;
    this.menuAnchor = anchor;
  }

  /** A combined pick sets every field: toggling would clear one already drilled. */
  applyOption = (option: DrilldownOption) => {
    const mode: DrilldownMode = option.drilldowns.length > 1 ? 'set' : 'toggle';
    for (const drilldown of option.drilldowns) this.args.onDrilldown?.(drilldown, mode);
    this.closeMenu();
  };

  closeMenu = () => {
    this.menuAnchor = undefined;
  };

  get pieChartType() {
    // Read the widget's donut flag directly — it's display-only, so toggling it
    // must apply without re-running the (memoized) query, unlike data.value.donut.
    return this.args.widget.donut ? 'donut' : 'pie';
  }

  /** Slice colors aligned to points order, honoring per-slice overrides. */
  get pieColors(): string[] {
    const overrides = this.format.colors ?? {};
    return this.piePoints.map(
      (p, i) => overrides[p.category] ?? DEFAULT_CHART_COLORS[i % DEFAULT_CHART_COLORS.length]!
    );
  }

  get isEmpty() {
    if (this.isLoading || this.isCounter) return false;
    if (this.isPie) return this.piePoints.length === 0;
    return this.combo.labels.length === 0;
  }

  /**
   * A param-kind SQL dataset on an unscoped dashboard can't be queried at all.
   * Checked here for EVERY widget type — tables self-fetch through
   * PreviewTable and never reach runWidget's guard, so without this an
   * imported SQL table would fire a doomed request and render a bare "no
   * data". Rendering the reason also makes runWidget's thrown message visible
   * instead of surfacing as an empty chart.
   */
  get scopeUnavailable() {
    return sqlScopeUnavailable(this.tableModelType, this.args.companyId);
  }

  get scopeMessage() {
    return SQL_SCOPE_REQUIRED_MESSAGE;
  }

  // ── format passthrough ──────────────────────────────────────────────────────
  get format() {
    return this.args.widget.format ?? {};
  }
  get leftFormatter() {
    return numberFormatter(this.format.yLeft?.valueFormat);
  }
  get rightFormatter() {
    return numberFormatter(this.format.yRight?.valueFormat);
  }
  /** Bar charts fill the host's box unless the widget pins its own chart height. */
  get fillsHeight() {
    return this.args.height != null && !this.format.height;
  }

  get legendPosition() {
    return this.format.legend?.position ?? 'bottom';
  }
  get showLegend() {
    return this.format.legend?.show !== false;
  }
  get showDataLabels() {
    return this.format.dataLabels?.show === true;
  }
  get xTitle() {
    return this.format.xAxis?.title;
  }
  get yTitle() {
    return this.format.yLeft?.title;
  }
  get yTitleRight() {
    return this.format.yRight?.title;
  }
  get labelAngle() {
    return this.format.xAxis?.labelAngle;
  }

  get seriesLabels(): string[] {
    if (this.isPie) return this.piePoints.map((p) => p.category);
    return [
      ...this.combo.barSeries.map((s) => s.label),
      ...this.combo.lineSeries.map((s) => s.label),
    ];
  }

  reportSeries = modifier((_el: Element, [labels]: [string[]]) => {
    const key = labels.join('|');
    if (key === this.#lastSeriesKey) return;
    this.#lastSeriesKey = key;
    // eslint-disable-next-line ember/no-runloop -- defer the series-label callback out of render to avoid a same-tick set
    next(this, () => this.args.onSeries?.(labels));
  });

  <template>
    <div
      data-test-widget-renderer
      class="relative h-full w-full min-h-0"
      {{this.reportSeries this.seriesLabels}}
      ...attributes
    >
      {{#if this.scopeUnavailable}}
        <div
          class="tio-row-center h-full w-full tio-fg-muted text-sm"
          data-test-widget-scope-required
        >
          {{this.scopeMessage}}
        </div>
      {{else if this.isLoading}}
        <div class="tio-row-center h-full w-full tio-fg-muted text-sm" data-test-widget-loading>
          Loading…
        </div>
      {{else if this.isCounter}}
        <SummaryDisplay
          @data={{this.counterPoints}}
          @label={{this.counterSubLabel}}
          @compact={{true}}
          @formatValue={{this.leftFormatter}}
        />
      {{else if this.isTable}}
        {{#if this.tableColumns.length}}
          {{! Contains the sticky header and sort menu; tiles themselves set no z-index so chart tooltips can escape. }}
          <div class="isolate h-full w-full overflow-auto" data-test-widget-table>
            <PreviewTable
              @columns={{this.tableColumns}}
              @allFields={{this.tableAllFields}}
              @filterValues={{this.effectiveFilters.userFilterValues}}
              @buildFilterValues={{this.effectiveFilters.buildFilterValues}}
              @buildFilterLayout={{this.effectiveFilters.buildFilterLayout}}
              @extraFilterValues={{this.effectiveFilters.extraFilterValues}}
              @companyId={{@companyId}}
              @companyFilterField={{this.tableCompanyFilterField}}
              @modelType={{this.tableModelType}}
              @sorts={{this.tableSorts}}
              @page={{this.tablePage}}
              @limit={{this.tableLimit}}
              @onSort={{this.updateTableSort}}
              @onPageChange={{this.updateTablePage}}
              @onLimitChange={{this.updateTableLimit}}
              @readOnly={{true}}
              @hideHeading={{true}}
              @isAggregated={{this.tableIsAggregated}}
              @groupByFieldKeys={{this.tableConfig.groupBy.selectedFields}}
              @columnAggregationMap={{this.tableConfig.groupBy.columnAggregations}}
              @dateGroupingIntervals={{this.tableConfig.groupBy.dateGroupingIntervals}}
              @columnFormatMap={{this.tableConfig.columnFormats}}
              @columnLabelMap={{this.tableConfig.columnLabels}}
              @columnAltTextMap={{this.tableConfig.columnAltTexts}}
              @onRowClick={{if this.canRowDrilldown this.handleRowClick}}
            />
          </div>
        {{else}}
          <div class="tio-row-center h-full w-full tio-fg-muted text-sm" data-test-widget-empty>
            Add columns from the field list, or start from a saved report.
          </div>
        {{/if}}
      {{else if this.isEmpty}}
        <div class="tio-row-center h-full w-full tio-fg-muted text-sm" data-test-widget-empty>
          {{if
            this.isPie
            "Pick a slice field and a value to see data."
            "Pick an X-axis and a measure to see data."
          }}
        </div>
      {{else if this.isPie}}
        <Chart
          @type={{this.pieChartType}}
          @data={{this.piePoints}}
          @colors={{this.pieColors}}
          @onClick={{if this.canDrilldown this.handlePieClick}}
          @selectedPoint={{this.selectedPiePoint}}
          @showLegend={{this.showLegend}}
          @legendPosition={{this.legendPosition}}
          @formatValue={{this.leftFormatter}}
          @height={{@height}}
        />
      {{else if this.isHorizontalChart}}
        {{! Categories on Y, values on X — value formatting/title come from the
            yLeft format (the value axis), the category title from xAxis. }}
        <HorizontalBarsChart
          @labels={{this.combo.labels}}
          @series={{this.combo.barSeries}}
          @stacked={{this.combo.stacked}}
          @onBarClick={{if this.canDrilldown this.handleBarClick}}
          @onLegendClick={{if this.canLegendDrilldown this.handleLegendClick}}
          @selectedBar={{this.selectedBar}}
          @height={{@height}}
          @fill={{this.fillsHeight}}
          @formatValue={{this.leftFormatter}}
          @xLabel={{this.yTitle}}
          @yLabel={{this.xTitle}}
          @showLegend={{this.showLegend}}
          @showDataLabels={{this.showDataLabels}}
        />
      {{else}}
        <ComboChart
          @labels={{this.combo.labels}}
          @barSeries={{this.combo.barSeries}}
          @lineSeries={{this.combo.lineSeries}}
          @stacked={{this.combo.stacked}}
          @secondaryAxis={{this.combo.secondaryAxis}}
          @onBarClick={{if this.canDrilldown this.handleBarClick}}
          @onLegendClick={{if this.canLegendDrilldown this.handleLegendClick}}
          @selectedBar={{this.selectedBar}}
          @height={{@height}}
          @fill={{this.fillsHeight}}
          @formatValue={{this.leftFormatter}}
          @formatValueRight={{this.rightFormatter}}
          @xLabel={{this.xTitle}}
          @yLabel={{this.yTitle}}
          @yLabelRight={{this.yTitleRight}}
          @labelAngle={{this.labelAngle}}
          @showLegend={{this.showLegend}}
          @legendPosition={{this.legendPosition}}
          @showDataLabels={{this.showDataLabels}}
        />
      {{/if}}
      {{#if this.canDrilldown}}
        <DrilldownMenu
          @anchor={{this.menuAnchor}}
          @options={{this.menuOptions}}
          @onPick={{this.applyOption}}
          @onClose={{this.closeMenu}}
        />
      {{/if}}
    </div>
  </template>
}
